/**
 * React component that renders our main board grid
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import isString from 'lodash/isString'
import { Row, Col } from 'reactstrap'
import BoardCard from '../BoardCard'
import LoadButton from 'client/shared/js/components/LoadButton'
import styles from './BoardCardsGrid.module.scss'

const BoardCardsGrid = ({
  user,
  boards,
  boardsOrder,
  boardActions,
  loadIndex,
  shouldLoadMore,
  onLoadMore,
  isLoading,
  anchor,
  disableFavorite
}) => {
  const handleLoadMore = useCallback(() => onLoadMore(loadIndex + 1), [loadIndex, onLoadMore])

  const LoadMoreButton = useMemo(() => (
    (shouldLoadMore || isLoading) ? (
      <Row>
        <Col className={cx(styles['load-more-container'], 'd-flex justify-content-center')}>
          <LoadButton isLoading={isLoading} onClick={handleLoadMore}>
            Load more trips
          </LoadButton>
        </Col>
      </Row>
    ) : null
  ), [handleLoadMore, shouldLoadMore, isLoading])

  const BoardCards = useMemo(() => (
    boardsOrder.map((currBoard) => {
      const boardId = isString(currBoard) ? currBoard : currBoard?.bid
      const board = boards[boardId]

      if (board) {
        return (
          <Col sm='12' md='6' lg='6' xl='4' key={`board-card-col-${boardId}`}>
            <BoardCard
              board={board}
              user={user}
              onFavoriteBoard={boardActions.followBoard}
              onUnfavoriteBoard={boardActions.unfollowBoard}
              anchor={anchor}
              disableFavorite={disableFavorite} />
          </Col>
        )
      }
    })
  ), [user, boards, boardsOrder, boardActions, anchor, disableFavorite])

  return (boardsOrder.length > 0) ? (
    <>
      <Row className={cx(styles['board-cards-grid'], 'board-cards-grid')}>
        {BoardCards}
      </Row>
      {LoadMoreButton}
    </>
  ) : null
}

BoardCardsGrid.defaultProps = {
  boards: {},
  boardsOrder: [],
  boardActions: {},
  loadIndex: 0,
  onLoadMore: () => false
}

BoardCardsGrid.propTypes = {
  user: PropTypes.object,
  boards: PropTypes.object,
  boardsOrder: PropTypes.array,
  boardActions: PropTypes.object,
  loadIndex: PropTypes.number,
  shouldLoadMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  isLoading: PropTypes.bool,
  anchor: PropTypes.bool,
  disableFavorite: PropTypes.bool
}

export default BoardCardsGrid
